import { SecurityConfig } from '@/installationConfig';
import Vue from 'vue';
import Router from 'vue-router';
import Home from './views/Home.vue';
import Unauthorized from './views/Unauthorized.vue';
import NotFound from './views/NotFound.vue';
import installationConfigService from './services/installationConfigService';

const getRouter: () => Promise<Router> = async () => {
  const $installationConfigService = new installationConfigService();

  await $installationConfigService.initSecurityConfig();
  await $installationConfigService.initAzureADOptions();

  Vue.use(Router);

  const router = new Router({
    mode: 'history',
    routes: [
      {
        path: '/',
        name: 'home',
        component: Home,
      },
      {
        path: '/unauthorized',
        name: 'unauthorized',
        meta: {
          allowUnauthorized: true,
        },
        component: Unauthorized,
      },
      {
        path: '/jobs',
        name: 'jobsOverview',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup],
        },
        component: () => import(/* webpackChunkName: "jobs" */ './modules/jobs/views/Index.vue'),
      },
      {
        path: '/externalInvoices',
        name: 'externalInvoicesOverview',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.purchaseRead],
        },
        component: () => import(/* webpackChunkName: "externalInvoices" */ './modules/externalInvoices/views/Overview.vue'),
      },
      {
        path: '/externalInvoices/:id',
        name: 'externalInvoicesDetails',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.purchaseRead],
        },
        component: () => import(/* webpackChunkName: "externalInvoices" */ './modules/externalInvoices/views/Details.vue'),
      },
      {
        path: '/alerts',
        name: 'alertsOverview',
        component: () => import(/* webpackChunkName: "alertsOverview" */ './modules/userAlerts/views/Overview.vue'),
      },
      {
        path: '/dashboards',
        name: 'dashboardsOverview',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup],
        },
        component: () => import(/* webpackChunkName: "dashboards" */ './modules/dashboard/views/Overview.vue'),
      },
      {
        path: '/dashboards/:id',
        name: 'dashboardDetails',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup],
        },
        component: () => import(/* webpackChunkName: "dashboards" */ './modules/dashboard/views/Details.vue'),
      },
      {
        path: '/actionpoints/:id',
        name: 'actionpointsDetails',
        component: () => import(/* webpackChunkName: "actionpoints" */ './modules/actionPoints/views/Details.vue'),
      },
      {
        path: '/tasks',
        name: 'tasksOverview',
        component: () => import(/* webpackChunkName: "tasks" */ './modules/shared/tasks/views/Overview.vue'),
      },
      {
        path: '/actionpoints',
        name: 'actionpointsOverview',
        component: () => import(/* webpackChunkName: "tasks" */ './modules/actionPoints/views/Overview.vue'),
      },
      {
        path: '/tasks/templates',
        name: 'tasktemplates',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.taskAdmin],
        },
        component: () => import(/* webpackChunkName: "tasksManagement" */ './modules/tasks/templates/views/Overview.vue'),
      },
      {
        path: '/tasks/templates/create',
        name: 'taskTemplateCreate',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.taskAdmin],
        },
        component: () => import(/* webpackChunkName: "tasksManagement" */ './modules/tasks/templates/views/Details.vue'),
      },
      {
        path: '/tasks/templates/:id',
        name: 'taskTemplateUpdate',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.taskAdmin],
        },
        component: () => import(/* webpackChunkName: "tasksManagement" */ './modules/tasks/templates/views/Details.vue'),
      },
      {
        path: '/tasks/procedures',
        name: 'taskprocedures',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.taskAdmin],
        },
        component: () => import(/* webpackChunkName: "tasksProc" */ './modules/tasks/procedures/views/Overview.vue'),
      },
      {
        path: '/tasks/procedures/create',
        name: 'taskprocedureCreate',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.taskAdmin],
        },
        component: () => import(/* webpackChunkName: "tasksProc" */ './modules/tasks/procedures/views/Details.vue'),
      },
      {
        path: '/tasks/procedures/:id',
        name: 'taskprocedureUpdate',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.taskAdmin],
        },
        component: () => import(/* webpackChunkName: "tasksProc" */ './modules/tasks/procedures/views/Details.vue'),
      },
      {
        path: '/tasks/:id',
        name: 'taskdetails',
        component: () => import(/* webpackChunkName: "tasks" */ './modules/shared/tasks/views/Details.vue'),
      },
      {
        path: '/agenda',
        name: 'agenda',
        component: () => import(/* webpackChunkName: "agenda" */ './modules/agenda/views/Overview.vue'),
      },
      {
        path: '/projectMeasuringStateOverview',
        name: 'projectMeasuringStateOverview',
        component: () => import(/* webpackChunkName: "agenda" */ './modules/projectMeasuringStateOverview/views/ProjectMeasuringStateOverview.vue'),
      },
      {
        path: '/rawmaterials',
        name: 'rawmaterialsOverview',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.articleReadWrite],
        },
        component: () => import(/* webpackChunkName: "rawmaterials" */ './views/rawmaterials/Overview.vue'),
      },
      {
        path: '/rawmaterials/add',
        name: 'rawmaterialsDetails',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.articleReadWrite],
        },
        component: () => import(/* webpackChunkName: "rawmaterials" */ './views/rawmaterials/Details.vue'),
      },
      {
        path: '/rawmaterials/:id',
        name: 'rawmaterialsEdit',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.articleReadWrite],
        },
        component: () => import(/* webpackChunkName: "rawmaterials" */ './views/rawmaterials/Details.vue'),
      },
      {
        path: '/articles',
        name: 'articlesOverview',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.articleReadWrite],
        },
        component: () => import(/* webpackChunkName: "articles" */ './modules/articles/views/Overview.vue'),
      },
      {
        path: '/articles/:id',
        name: 'editArticle',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.articleReadWrite],
        },
        component: () => import(/* webpackChunkName: "articles" */ './modules/articles/views/Details.vue'),
      },
      {
        path: '/contacts',
        name: 'contactsOverview',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.administrationGroup, SecurityConfig.projectCoordinatorGroup],
        },

        component: () => import(/* webpackChunkName: "contactsOverview" */ './views/crm/contacts/Overview.vue'),
      },
      {
        path: '/contacts/:id',
        name: 'contactDetails',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.administrationGroup, SecurityConfig.projectCoordinatorGroup],
        },
        component: () => import(/* webpackChunkName: "contactDetails" */ './views/crm/contacts/Details.vue'),
      },
      {
        path: '/employees',
        name: 'employeesOverview',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup],
        },
        component: () => import(/* webpackChunkName: "employees" */ './modules/employees/views/Overview.vue'),
      },
      {
        path: '/employees/:id',
        name: 'employeesDetails',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup],
        },
        component: () => import(/* webpackChunkName: "employees" */ './modules/employees/views/Details.vue'),
      },
      {
        path: '/companies/:id',
        name: 'companyDetails',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.administrationGroup, SecurityConfig.projectCoordinatorGroup],
        },
        component: () => import(/* webpackChunkName: "companyDetails" */ './views/crm/companies/Details.vue'),
      },
      {
        path: '/subcontractors/:id',
        name: 'subcontractorDetails',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.administrationGroup, SecurityConfig.projectCoordinatorGroup],
        },
        component: () => import(/* webpackChunkName: "companyDetails" */ './views/crm/subcontractors/Details.vue'),
      },
      {
        path: '/projects',
        name: 'projectsOverview',
        component: () => import(/* webpackChunkName: "projectsOverview" */ './views/projects/Overview.vue'),
      },
      {
        path: '/projects/add',
        name: 'projectAdd',
        component: () => import(/* webpackChunkName: "projectAdd" */ './views/projects/Add.vue'),
      },
      {
        path: '/projects/:id',
        name: 'projectDetails',
        component: () => import(/* webpackChunkName: "projectDetails" */ './views/projects/Details.vue'),
      },
      {
        path: '/quotations',
        name: 'quotationsOverview',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.quotationRead],
        },
        component: () => import(/* webpackChunkName: "quotations" */ './modules/quotations/views/Overview.vue'),
      },
      {
        path: '/quotations/:id',
        name: 'quotationDetails',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.quotationRead],
        },
        component: () => import(/* webpackChunkName: "quotations" */ './modules/quotations/views/Details.vue'),
      },
      {
        path: '/orders',
        name: 'ordersOverview',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.purchaseOrderRead],
        },
        component: () => import(/* webpackChunkName: "orders" */ './modules/orders/views/Overview.vue'),
      },
      {
        path: '/orders/:id',
        name: 'ordersDetails',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.purchaseOrderRead],
        },
        component: () => import(/* webpackChunkName: "orders" */ './modules/orders/views/Details.vue'),
      },
      {
        path: '/creditnotes',
        name: 'creditnotesOverview',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.invoicingRead],
        },
        component: () => import(/* webpackChunkName: "invoices" */ './modules/creditnotes/views/Overview.vue'),
      },
      {
        path: '/creditnotes/:id',
        name: 'creditnotesDetails',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.invoicingRead],
        },
        component: () => import(/* webpackChunkName: "invoices" */ './modules/creditnotes/views/Details.vue'),
      },
      {
        path: '/invoices',
        name: 'invoicesOverview',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.invoicingRead],
        },
        component: () => import(/* webpackChunkName: "invoices" */ './modules/invoicing/views/Overview.vue'),
      },
      {
        path: '/invoices/:id',
        name: 'invoiceDetails',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.invoicingRead],
        },
        component: () => import(/* webpackChunkName: "invoicing" */ './modules/invoicing/views/Details.vue'),
      },
      {
        path: '/progressStatements/:documentNumber',
        name: 'progressStatementDetails',
        component: () => import(/* webpackChunkName: "progressStatementDetails" */ './modules/progressStatements/views/Details.vue'),
      },
      {
        path: '/projects/:id/measurements/:name',
        name: 'measurementDetails',
        component: () => import(/* webpackChunkName: "projectDetails" */ './modules/measurements/views/Details.vue'),
      },
      {
        path: '/contracts/:documentNumber',
        name: 'contractDetails',
        component: () => import(/* webpackChunkName: "Contract" */ './modules/projects/contracts/views/Details.vue'),
      },
      {
        path: '/measuringstates/:name',
        name: 'measuringstateDetails',
        component: () => import(/* webpackChunkName: "measuringstates" */ './modules/measuringstates/views/details.vue'),
      },
      {
        path: '/machinery',
        name: 'machineryOverview',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.machineryRead],
        },
        component: () => import(/* webpackChunkName: "machinery" */ './modules/machinery/views/Overview.vue'),
      },
      {
        path: '/settings/machinerystatus',
        name: 'machineryStatusOverview',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.machineryReadWrite],
        },
        component: () => import(/* webpackChunkName: "machinery" */ './modules/machinery/views/MachineryStatus.vue'),
      },
      {
        path: '/machinery/:id',
        name: 'machineryDetails',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.machineryRead],
        },
        component: () => import(/* webpackChunkName: "machinery" */ './modules/machinery/views/Details.vue'),
      },
      {
        path: '/machinery/:machineryId/rentals/:id',
        name: 'machineryRentalDetails',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.machineryRead],
        },
        component: () => import(/* webpackChunkName: "machineryRentalDetails" */ './modules/machinery/views/MachineryRental.vue'),
      },
      {
        path: '/machinery/:machineryId/events/:id',
        name: 'machineryEventDetails',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.machineryRead],
        },
        component: () => import(/* webpackChunkName: "machineryEventDetails" */ './modules/machinery/views/MachineryEvent.vue'),
      },
      {
        path: '/settings',
        name: 'settingsPage',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup],
        },
        component: () => import(/* webpackChunkName: "settings" */ './modules/settings/views/Settings.vue'),
      },
      {
        path: '/settings/machineryeventtypes',
        name: 'machineryeventtypes',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.machineryReadWrite],
        },
        component: () => import(/* webpackChunkName: "machineryeventtypes" */ './modules/machinery/views/MachineryEventTypes.vue'),
      },
      {
        path: '/settings/machineryprofiles',
        name: 'machineryprofiles',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.machineryReadWrite],
        },
        component: () => import(/* webpackChunkName: "machineryprofiles" */ './modules/machinery/views/MachineryProfiles.vue'),
      },
      {
        path: '/settings/machineryprofiles/:id',
        name: 'machineryprofile',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.machineryReadWrite],
        },
        component: () => import(/* webpackChunkName: "machineryprofile" */ './modules/machinery/views/MachineryProfile.vue'),
      },
      {
        path: '/settings/catalogs',
        name: 'tagssettings',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup],
        },
        component: () => import(/* webpackChunkName: "tagssettings" */ './views/settings/Catalogs.vue'),
      },
      {
        path: '/settings/quotationconditions',
        name: 'quotationconditionssettings',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup],
        },
        component: () => import(
          './views/settings/QuotationConditions.vue'),
      },
      {
        path: '/settings/counters',
        name: 'counterssettings',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup],
        },
        component: () => import(/* webpackChunkName: "counterssettings" */ './views/settings/Counters.vue'),
      },
      {
        path: '/settings/mailtemplates',
        name: 'mailtemplates',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup],
        },
        component: () => import(/* webpackChunkName: "mailtemplates" */ './modules/settings/mailtemplates/views/Overview.vue'),
      },
      {
        path: '/settings/tunnelgroups',
        name: 'tunnelgroups',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup],
        },
        component: () => import(/* webpackChunkName: "tunnelgroups" */ './modules/tunnelGroups/views/Overview.vue'),
      },
      {
        path: '/articlecategories',
        name: 'articlecategories',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.articleReadWrite],
        },
        component: () => import(/* webpackChunkName: "articlecategories" */ './modules/articleCategories/views/Overview.vue'),
      },
      {
        path: '/followup',
        name: 'followup',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.administrationGroup, SecurityConfig.qualityGuardGroup, SecurityConfig.qualityGuardOneSkinGroup, SecurityConfig.projectCoordinatorGroup],
        },
        component: () => import(/* webpackChunkName: "followupoverview" */ './views/followup/Overview.vue'),
      },
      {
        path: '/projectstoprogressoverview',
        name: 'projectstoprogressoverview',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.administrationGroup, SecurityConfig.projectCoordinatorGroup],
        },
        component: () => import(/* webpackChunkName: "projectstoprogressoverview" */ './views/projectstoprogress/Overview.vue'),
      },
      {
        path: '/progressstatementfollowup',
        name: 'progressstatementfollowup',
        meta: {
          allowedRoles: [SecurityConfig.administratorGroup, SecurityConfig.administrationGroup, SecurityConfig.projectCoordinatorGroup],
        },
        component: () => import(/* webpackChunkName: "progressstatementfollowupoverview" */ './views/progressstatementfollowup/Overview.vue'),
      },
      { path: '*', component: NotFound },
    ],
  });

  return router;
};

export default getRouter;